import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Tabs, Form, Input, InputNumber, Select, Switch, Button, Row, Col,
  Space, message, Tag, Table, Alert, Modal, Typography, Spin, Steps
} from 'antd';
import {
  SearchOutlined, PlusOutlined, DeleteOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined,
  SaveOutlined, CloseOutlined, EditOutlined,
  LeftOutlined, RightOutlined
} from '@ant-design/icons';
import {
  GST_REGISTRATION_TYPES,
  SEZ_TYPES,
  STATE_SELECT_OPTIONS,
  CUSTOMER_CATEGORY_SUGGESTIONS,
  MSME_CATEGORY_OPTIONS,
  GST_FILING_FREQUENCY_OPTIONS,
  TDS_SECTION_OPTIONS,
  // Stage 6A.2K — Price Group placeholder options + strict India mobile rule.
  MOBILE_INDIA_REGEX,
  stateNameFromCode,
  isValidGstin
} from './customerConstants';
import {
  digitsOnly, gstinInput, isEmail
} from '../../../../utils/validators';
import { customerApi } from './customerApi';
import priceListApi from '../priceLists/priceListApi';
import './CustomerTabbedDrawer.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Customer Master Add/Edit/View tabbed popup (Stage 6A.2D-Popup).
//
// FILE NAMING NOTE
//   The component file is still named `CustomerTabbedDrawer.jsx` for
//   import-path stability. The visual surface is now a centered AntD
//   Modal, NOT a right-side Drawer. The "Drawer" in the filename is
//   historical only — do not let it mislead you.
//
// Stage 6A.2D-Popup — classic ERP popup redesign:
//   • Two flat panels (left = identity + contact + address;
//     right = business, sales, GST flags, wastage). Card-heavy
//     SectionCard layout removed in favour of `<div .erp-customer-popup__panel>`.
//   • SINGLE textbox "GSTIN / Aadhaar / PAN / URP" replaces the
//     separate GSTIN search bar + URP KYC card + Legal Name / Trade
//     Name visible fields. `parseTaxIdentity()` routes the value to
//     the correct backend column at submit-time.
//   • Customer Type radio (Individual / Business) added to the right
//     panel (it was a hidden default before).
//   • Customer Code field stays HIDDEN — backend generates CUST-NNNNNN.
//     The popup header shows the assigned code as a muted tag after
//     save / in Edit mode.
//   • Centered Modal, width 1080. Internal body scroll (maxHeight
//     70vh). Header + footer pinned. Footer is Save + Close.
//   • Aadhaar still encrypted at rest (mapper + secrets.encrypt).
//     The popup never exposes the full Aadhaar; existing rows show
//     a masked "XXXX-XXXX-NNNN" in the single input.
// ─────────────────────────────────────────────────────────────────

const { TabPane } = Tabs;
const { Text } = Typography;

// Default values for ALL form fields. Keeps unrelated tabs from
// becoming undefined when the user only fills Tab 1.
const DEFAULTS = Object.freeze({
  // Stage 6A.2E.3 — customerType field removed from the form. Backend
  // column keeps its DB DEFAULT ('business') for legacy rows.
  gstRegistrationType: 'unregistered',
  // Stage 6A.2K — Price Group defaults to null; the dropdown is
  // clearable and the backend column is nullable.
  priceGroup:          null,
  status: 'active',
  customerStatusAxis: 'active',
  isActive: true,
  isSez: false,
  isCompositionDealer: false,
  isReverseChargeApplicable: false,
  sameAsBilling: true,
  unloadingRequired: false,
  specialPackingRequired: false,
  billingCountry: 'India',
  shippingCountry: 'India',
  creditLimit: 0,
  creditDays: 0,
  openingBalance: 0,
  // Stage 6A.2I — Balance Type is now MANDATORY in Add/Edit. Default to
  // 'debit' (the receivables side) so the user gets a sensible starting
  // pick; the validator still requires an explicit value at submit time.
  openingBalanceType: 'debit',
  preferredGlassTypes: [],
  // Stage 6A.2C — URP KYC defaults
  kycVerified: false,
  kycVerificationStatus: 'not_verified',
  salesRepresentativeId: null,
  // HRMS salesperson (employee) — distinct from the user-based Sales Rep.
  salespersonEmployeeId: null,
  // Stage 6A.2E.2 — GSTIN textbox accepts only a GSTIN or "URP".
  // Aadhaar / PAN is collected in a separate textbox below.
  taxIdentityInput:   '',
  aadhaarOrPanInput: '',
  // Wastage defaults
  wastageDefaults: {
    widthRoundOff: 0,
    heightRoundOff: 0,
    widthPlusMm: 0,
    heightPlusMm: 0
  },
  // Stage 6A.2D-Popup-Light — GST Law 2026 accounting defaults
  tdsApplicable:           false,
  tdsSection:              null,
  tcsApplicable:           false,
  section206abApplicable:  false,
  msmeRegistered:          false,
  udyamNumber:             null,
  msmeCategory:            null,
  gstFilingFrequency:      null,
  eInvoiceApplicable:      false,
  eWayBillApplicable:      false,
  bankName:                null,
  bankAccountNumber:       null,
  bankIfscCode:            null,
  bankBranch:              null,
  defaultLedgerGroup:      'Sundry Debtors'
});

function uid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Stage 6A.2D-Popup — single-input tax-identity parser.
//
// The redesigned popup hides explicit Aadhaar / PAN / URP fields
// behind ONE textbox. This helper inspects the raw value the user
// typed and tells the payload builder which backend column to route
// it to.
//
// Recognised shapes:
//   "URP"                              -> { kind: 'urp' }
//   15-char GSTIN                      -> { kind: 'gstin',   value }
//   10-char PAN                        -> { kind: 'pan',     value }
//   12 digits (spaces/hyphens allowed) -> { kind: 'aadhaar', value (12 digits) }
//   "XXXX-XXXX-9012" (masked re-render)-> { kind: 'aadhaar_unchanged' }
//   "NIL" / "NILL"                     -> { kind: 'nil' }       (Stage 6A.2I)
//   empty                              -> { kind: 'empty' }
//   anything else                      -> { kind: 'unknown', raw }
//
// `aadhaar_unchanged` keeps the on-file encrypted Aadhaar safe when an
// Edit-mode submission leaves the masked value untouched.
//
// Stage 6A.2I — NIL / NILL are an explicit "KYC document not provided"
// declaration. The drawer routes the save through the Approval MFA
// hook before calling the backend so the L1 step-up gate fires.
function parseTaxIdentity(raw) {
  if (raw === null || raw === undefined || raw === '') return { kind: 'empty' };
  const trimmed = String(raw).trim();
  const upper   = trimmed.toUpperCase();
  if (upper === 'URP') return { kind: 'urp' };
  // Stage 6A.2I — NIL & NILL both resolve to the same payload shape.
  if (upper === 'NIL' || upper === 'NILL') return { kind: 'nil' };
  if (/^X{4}-X{4}-[0-9]{4}$/i.test(trimmed)) return { kind: 'aadhaar_unchanged' };
  if (/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][A-Z][0-9A-Z]$/.test(upper)) {
    return { kind: 'gstin', value: upper };
  }
  if (/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(upper)) {
    return { kind: 'pan', value: upper };
  }
  const digits = trimmed.replace(/[\s-]/g, '');
  if (/^[0-9]{12}$/.test(digits)) return { kind: 'aadhaar', value: digits };
  return { kind: 'unknown', raw: trimmed };
}

// Stage 6A.2D-Popup — the standalone Aadhaar formatter + digit-only
// helpers were retired with the URP KYC card. parseTaxIdentity() now
// owns all identity-input normalisation.

// Stage 6A.2E.2 — the GSTIN textbox now accepts ONLY a 15-char GSTIN
// or the literal "URP". Aadhaar / PAN data is collected in a separate
// textbox. `parseGstinField` reduces parseTaxIdentity's wider output
// to the subset that's valid for the GSTIN field, so the rest of the
// form can branch cleanly.
function parseGstinField(raw) {
  const parsed = parseTaxIdentity(raw);
  if (parsed.kind === 'gstin' || parsed.kind === 'urp' || parsed.kind === 'empty') {
    return parsed;
  }
  // Anything else typed in the GSTIN box is treated as unrecognised.
  return { kind: 'unknown', raw: parsed.raw || String(raw || '').trim() };
}

// ─── Address Edit Modal (popup CRUD) ──────────────────────────
function AddressEditModal({ open, record, onCancel, onSave }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(record || {
        addressType: 'shipping',
        tradeName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        pincode: '',
        state: '',
        stateCode: '',
        gstin: '',
        isDefault: false,
        isActive: true
      });
    }
  }, [open, record, form]);

  async function handleOk() {
    try {
      const v = await form.validateFields();
      onSave({ ...(record || {}), ...v });
    } catch (_e) { /* validation rendered inline */ }
  }

  return (
    <Modal
      open={open}
      title={record && record.id ? 'Edit address' : 'Add address'}
      onCancel={onCancel}
      onOk={handleOk}
      width={680}
      destroyOnClose
      okText="Save"
      cancelText="Cancel"
    >
      <Form form={form} layout="vertical" className="erp-customer-drawer__form">
        <Row gutter={8}>
          <Col span={8}>
            <Form.Item label="Type" name="addressType">
              <Select options={[{ value: 'shipping', label: 'Shipping' }]} />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item label="Trade Name" name="tradeName"
              rules={[{ max: 200 }]}>
              <Input maxLength={200} placeholder="At-site name" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Address Line 1" name="addressLine1" rules={[{ max: 250 }]}>
          <Input maxLength={250} />
        </Form.Item>
        <Form.Item label="Address Line 2" name="addressLine2" rules={[{ max: 250 }]}>
          <Input maxLength={250} />
        </Form.Item>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item label="City" name="city" rules={[{ max: 100 }]}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Pincode" name="pincode"
              getValueFromEvent={digitsOnly(6)}
              rules={[{
                validator: (_r, v) => !v || /^[0-9]{6}$/.test(String(v))
                  ? Promise.resolve()
                  : Promise.reject(new Error('Pincode must be 6 digits.'))
              }]}>
              <Input maxLength={6} inputMode="numeric" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="State" name="stateCode">
          <Select showSearch allowClear
            options={STATE_SELECT_OPTIONS}
            optionFilterProp="label"
            placeholder="Select state"
            onChange={(code) => {
              if (code) form.setFieldValue('state', stateNameFromCode(code));
            }} />
        </Form.Item>
        <Form.Item label="GSTIN (optional)" name="gstin"
          getValueFromEvent={gstinInput}
          rules={[{
            // Match the KYC GSTIN rule: blank is fine, but a typed value
            // must be a valid 15-char GSTIN — caught here inline rather than
            // surfacing as a generic backend rejection on save.
            validator: (_r, v) => !v || isValidGstin(String(v).toUpperCase())
              ? Promise.resolve()
              : Promise.reject(new Error('Please enter a valid 15-character GSTIN (or leave it blank).'))
          }]}>
          <Input placeholder="27AAAPL1234C1Z5" maxLength={15} />
        </Form.Item>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item label="Default Address" name="isDefault" valuePropName="checked">
              <Switch size="small" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch size="small" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// Map a Joi error path onto a human section/row so a nested rejection
// (e.g. a Shipping Address or Contact GSTIN) points the operator to the
// right tab instead of looking like a top-level field error. Returns ''
// for top-level fields — their message already names the field.
function customerIssueSection(path) {
  if (!Array.isArray(path) || !path.length) return '';
  const head = path[0];
  const idx = typeof path[1] === 'number' ? path[1] + 1 : null;
  if (head === 'addresses') return idx ? `Shipping address ${idx}` : 'Shipping address';
  if (head === 'contacts') return idx ? `Contact ${idx}` : 'Contact';
  if (head === 'wastageDefaults') return 'Wastage defaults';
  return '';
}

// Turn backend Joi `details` ({message, path, type}) into operator-facing
// lines: prefix nested fields with their section/row, and collapse the
// redundant "exactly N characters" when a stricter format message exists
// for the same field (GSTIN fails both length + pattern under abortEarly:false).
function formatCustomerBackendIssues(details) {
  const byPath = new Map();
  for (const d of details) {
    if (!d || !d.message) continue;
    const key = Array.isArray(d.path) ? d.path.join('.') : String(d.path || '');
    if (!byPath.has(key)) byPath.set(key, []);
    byPath.get(key).push(d);
  }
  const out = [];
  for (const group of byPath.values()) {
    const hasPattern = group.some((d) => d.type === 'string.pattern.base');
    const chosen = hasPattern ? group.filter((d) => d.type !== 'string.length') : group;
    for (const d of chosen) {
      const loc = customerIssueSection(d.path);
      out.push(loc ? `${loc} — ${d.message}` : d.message);
    }
  }
  return out;
}

// ─── Tab 2: Other Addresses — read-only grid + popup CRUD ────
function AddressesTable({ value = [], onChange, readOnly }) {
  const rows = value;
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function openAdd() {
    setEditing(null);
    setEditorOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setEditorOpen(true);
  }
  function saveRow(patch) {
    if (editing && editing._key) {
      onChange(rows.map((r) => r._key === editing._key ? { ...r, ...patch } : r));
    } else {
      onChange([
        ...rows,
        { _key: uid(), deleted: false, ...patch }
      ]);
    }
    setEditorOpen(false);
    setEditing(null);
  }
  function removeRow(rowKey) {
    const target = rows.find((r) => r._key === rowKey);
    if (!target) return;
    Modal.confirm({
      title: 'Remove this address?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Remove', okButtonProps: { danger: true },
      onOk: () => {
        if (target.id) {
          onChange(rows.map((r) => r._key === rowKey ? { ...r, deleted: true } : r));
        } else {
          onChange(rows.filter((r) => r._key !== rowKey));
        }
      }
    });
  }

  const columns = [
    {
      title: 'Type', dataIndex: 'addressType', width: 90,
      render: (v) => <Tag style={{ marginInlineEnd: 0 }}>{(v || 'shipping').toUpperCase()}</Tag>
    },
    { title: 'Trade Name', dataIndex: 'tradeName', width: 170,
      render: (v) => v || '—' },
    { title: 'Address Line 1', dataIndex: 'addressLine1', width: 220,
      render: (v) => v || '—' },
    { title: 'Address Line 2', dataIndex: 'addressLine2', width: 180,
      render: (v) => v || '—' },
    { title: 'City', dataIndex: 'city', width: 120,
      render: (v) => v || '—' },
    { title: 'Pincode', dataIndex: 'pincode', width: 80,
      render: (v) => v || '—' },
    {
      title: 'State', dataIndex: 'stateCode', width: 160,
      render: (v) => v ? `${stateNameFromCode(v) || ''} (${v})` : '—'
    },
    { title: 'GSTIN', dataIndex: 'gstin', width: 150,
      render: (v) => v || '—' },
    {
      title: 'Default', dataIndex: 'isDefault', width: 70, align: 'center',
      render: (v) => v ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : '—'
    },
    !readOnly && {
      title: '', width: 78, fixed: 'right',
      render: (_v, r) => r.deleted ? (
        <Tag color="warning" style={{ marginInlineEnd: 0 }}>queued</Tag>
      ) : (
        <Space size={4}>
          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => openEdit(r)} />
          <Button size="small" type="text" danger icon={<DeleteOutlined />}
            onClick={() => removeRow(r._key)} />
        </Space>
      )
    }
  ].filter(Boolean);

  return (
    <div className="erp-customer-drawer__excel-grid">
      <Space style={{ marginBottom: 8 }}>
        {!readOnly && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>
            Add address
          </Button>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Only shipping addresses are supported today. Use the row actions to edit / remove.
        </Text>
      </Space>
      <Table
        size="small" rowKey="_key"
        dataSource={rows.filter((r) => !r.deleted)}
        columns={columns}
        pagination={false}
        scroll={{ x: 1180 }}
        sticky bordered
        locale={{ emptyText: 'No shipping addresses added yet.' }}
      />
      <AddressEditModal
        open={editorOpen}
        record={editing}
        onCancel={() => { setEditorOpen(false); setEditing(null); }}
        onSave={saveRow}
      />
    </div>
  );
}

// ─── Contact Edit Modal (popup CRUD) ──────────────────────────
function ContactEditModal({ open, record, onCancel, onSave }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(record || {
        contactName: '',
        designation: '',
        department: '',
        mobile: '',
        alternateMobile: '',
        email: '',
        isPrimary: false,
        isActive: true,
        remarks: ''
      });
    }
  }, [open, record, form]);

  async function handleOk() {
    try {
      const v = await form.validateFields();
      onSave({ ...(record || {}), ...v });
    } catch (_e) { /* validation rendered inline */ }
  }

  return (
    <Modal
      open={open}
      title={record && record.id ? 'Edit contact' : 'Add contact'}
      onCancel={onCancel}
      onOk={handleOk}
      width={680}
      destroyOnClose
      okText="Save"
      cancelText="Cancel"
    >
      <Form form={form} layout="vertical" className="erp-customer-drawer__form">
        <Form.Item label="Name" name="contactName"
          rules={[{ required: true, message: 'Contact name is required.' }, { max: 150 }]}>
          <Input maxLength={150} />
        </Form.Item>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item label="Designation" name="designation" rules={[{ max: 100 }]}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Department" name="department" rules={[{ max: 100 }]}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item label="Mobile" name="mobile"
              getValueFromEvent={digitsOnly(10)}
              rules={[{
                // Stage 6A.2K — strict 10-digit Indian mobile.
                validator: (_r, v) => !v || MOBILE_INDIA_REGEX.test(String(v))
                  ? Promise.resolve()
                  : Promise.reject(new Error(
                      'Mobile number must be exactly 10 digits and start with 9, 8, 7, or 6.'
                    ))
              }]}>
              <Input maxLength={10} inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Alternate Mobile" name="alternateMobile"
              getValueFromEvent={digitsOnly(10)}
              rules={[{
                // Stage 6A.2K — strict 10-digit Indian mobile.
                validator: (_r, v) => !v || MOBILE_INDIA_REGEX.test(String(v))
                  ? Promise.resolve()
                  : Promise.reject(new Error(
                      'Mobile number must be exactly 10 digits and start with 9, 8, 7, or 6.'
                    ))
              }]}>
              <Input maxLength={10} inputMode="numeric" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Email" name="email"
          rules={[{
            validator: (_r, v) => !v || isEmail(v)
              ? Promise.resolve()
              : Promise.reject(new Error('Invalid email.'))
          }]}>
          <Input maxLength={150} />
        </Form.Item>
        <Form.Item label="Remarks" name="remarks" rules={[{ max: 2000 }]}>
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item label="Primary Contact" name="isPrimary" valuePropName="checked"
              tooltip="Only one contact can be primary. Saving primary on this row clears it on siblings.">
              <Switch size="small" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch size="small" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ─── Tab 3: Contact Details — read-only grid + popup CRUD ─────
function ContactsTable({ value = [], onChange, readOnly }) {
  const rows = value;
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function openAdd() {
    setEditing(null);
    setEditorOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setEditorOpen(true);
  }
  function saveRow(patch) {
    // Enforce single primary across siblings.
    let next;
    if (editing && editing._key) {
      next = rows.map((r) => r._key === editing._key ? { ...r, ...patch } : r);
    } else {
      next = [...rows, { _key: uid(), deleted: false, ...patch }];
    }
    if (patch.isPrimary === true) {
      next = next.map((r) =>
        r._key === (editing && editing._key) || (!editing && r._key === next[next.length - 1]._key)
          ? r
          : { ...r, isPrimary: false }
      );
    }
    onChange(next);
    setEditorOpen(false);
    setEditing(null);
  }
  function removeRow(rowKey) {
    const target = rows.find((r) => r._key === rowKey);
    if (!target) return;
    Modal.confirm({
      title: 'Remove this contact?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Remove', okButtonProps: { danger: true },
      onOk: () => {
        if (target.id) {
          onChange(rows.map((r) => r._key === rowKey ? { ...r, deleted: true } : r));
        } else {
          onChange(rows.filter((r) => r._key !== rowKey));
        }
      }
    });
  }

  const columns = [
    { title: 'Name',        dataIndex: 'contactName',     width: 160, render: (v) => v || '—' },
    { title: 'Designation', dataIndex: 'designation',     width: 130, render: (v) => v || '—' },
    { title: 'Department',  dataIndex: 'department',      width: 120, render: (v) => v || '—' },
    { title: 'Mobile',      dataIndex: 'mobile',          width: 120, render: (v) => v || '—' },
    { title: 'Alt. Mobile', dataIndex: 'alternateMobile', width: 120, render: (v) => v || '—' },
    { title: 'Email',       dataIndex: 'email',           width: 200, render: (v) => v || '—' },
    {
      title: 'Primary', dataIndex: 'isPrimary', width: 72, align: 'center',
      render: (v) => v ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : '—'
    },
    {
      title: 'Active', dataIndex: 'isActive', width: 65, align: 'center',
      render: (v) => v !== false ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : '—'
    },
    !readOnly && {
      title: '', width: 78, fixed: 'right',
      render: (_v, r) => r.deleted ? (
        <Tag color="warning" style={{ marginInlineEnd: 0 }}>queued</Tag>
      ) : (
        <Space size={4}>
          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => openEdit(r)} />
          <Button size="small" type="text" danger icon={<DeleteOutlined />}
            onClick={() => removeRow(r._key)} />
        </Space>
      )
    }
  ].filter(Boolean);

  return (
    <div className="erp-customer-drawer__excel-grid">
      <Space style={{ marginBottom: 8 }}>
        {!readOnly && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>
            Add contact
          </Button>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Only one contact can be marked Primary. Use the row actions to edit / remove.
        </Text>
      </Space>
      <Table
        size="small" rowKey="_key"
        dataSource={rows.filter((r) => !r.deleted)}
        columns={columns}
        pagination={false}
        scroll={{ x: 1180 }}
        sticky bordered
        locale={{ emptyText: 'No contacts added yet.' }}
      />
      <ContactEditModal
        open={editorOpen}
        record={editing}
        onCancel={() => { setEditorOpen(false); setEditing(null); }}
        onSave={saveRow}
      />
    </div>
  );
}

// Stage 6A.2D-Popup — SectionCard removed. The flat two-panel
// layout no longer uses card-headers; see
// `.erp-customer-popup__panel` in CustomerTabbedDrawer.css.

// ─── Tab 1: Customer Details — basic operational info ────────
//
// Stage 6A.2E reorganization:
//   • GSTIN/Aadhaar/PAN/URP, GST registration type, GST flags,
//     Credit Limit/Days, Opening Balance, Balance Type, Payment
//     Terms all moved to the GST & Accounting tab.
//   • Default Wastage Mode moved to its own tab.
//   • This tab now holds ONLY the basic operational info: name,
//     type, category, parent customer, sales rep, status, address,
//     phone/mobile, CSV mobiles, CSV emails, remarks.
function CustomerDetailsTab({
  form, mode,
  priceLists = [], priceListsLoading = false, priceListLocked = false, assignedList = null,
  salesRepOptions, salesRepLoading, onSalesRepSearch,
  salespersonOptions = [], salespersonLoading = false,
  parentOptions, parentLoading, onParentSearch,
  onGstinSearch, gstinBusy, gstinResult
}) {
  const isView = mode === 'view';
  const customerName = Form.useWatch('customerName', form);
  const taxIdentityInput = Form.useWatch('taxIdentityInput', form);
  // Stage 6A.2I — watch the Aadhaar / PAN field so we can surface the
  // NIL banner + drive the Approval MFA flow at Save time.
  const aadhaarOrPanInput = Form.useWatch('aadhaarOrPanInput', form);
  const isNilKyc = (() => {
    const s = String(aadhaarOrPanInput || '').trim().toUpperCase();
    return s === 'NIL' || s === 'NILL';
  })();
  const prevCustomerNameRef = useRef('');
  const prevDerivedPanRef = useRef('');

  // Stage 6A.2E.2 — GSTIN-field classifier accepts only GSTIN or URP.
  // The chip strip + Search button enable / GST Registration Type
  // auto-set all key off this reduced shape.
  const parsedLive = parseGstinField(taxIdentityInput);
  const isGstin = parsedLive.kind === 'gstin';

  // Stage 6A.2D-Popup-Light — Display Name mirrors Customer Name by
  // default. We only auto-fill when the user hasn't yet typed a custom
  // display name (display name is empty OR still matches the previous
  // customer-name value).
  useEffect(() => {
    if (isView) return;
    const currentDisplay = form.getFieldValue('displayName') || '';
    const prevName = prevCustomerNameRef.current;
    const nextName = customerName || '';
    if (!currentDisplay || currentDisplay === prevName) {
      form.setFieldValue('displayName', nextName);
    }
    prevCustomerNameRef.current = nextName;
  }, [customerName, form, isView]);

  // Stage 6A.2E.1 — auto-set GST Registration Type from the GSTIN
  // textbox so the matching dropdown beside it stays accurate.
  useEffect(() => {
    if (isView) return;
    const cur = form.getFieldValue('gstRegistrationType');
    if (parsedLive.kind === 'gstin') {
      if (!cur || cur === 'unregistered') {
        form.setFieldValue('gstRegistrationType', 'registered_regular');
      }
    } else if (parsedLive.kind === 'urp') {
      if (cur !== 'unregistered') {
        form.setFieldValue('gstRegistrationType', 'unregistered');
      }
    }
  }, [taxIdentityInput, form, isView, parsedLive.kind]);

  // Stage 6A.2E.4 — when GSTIN is valid, the Aadhaar / PAN textbox
  // mirrors the COMPLETE GSTIN (not just chars 3-12). It's a display
  // aid only — backend still derives PAN from main.gstin and the
  // mapper writes the right column. We only auto-fill when the
  // Aadhaar/PAN field is empty OR still equals the previously
  // auto-set value (so a user who types something custom isn't
  // clobbered).
  useEffect(() => {
    if (isView) return;
    if (parsedLive.kind === 'gstin') {
      const full = parsedLive.value;
      if (!full) return;
      const cur = form.getFieldValue('aadhaarOrPanInput') || '';
      const prev = prevDerivedPanRef.current;
      if (!cur || cur === prev) {
        form.setFieldValue('aadhaarOrPanInput', full);
      }
      prevDerivedPanRef.current = full;
    } else if (parsedLive.kind === 'urp') {
      // Wipe the auto-copied GSTIN when the customer is flipped back
      // to URP — but DON'T clobber a manually-typed PAN/Aadhaar.
      const cur = form.getFieldValue('aadhaarOrPanInput') || '';
      const prev = prevDerivedPanRef.current;
      if (cur && cur === prev) {
        form.setFieldValue('aadhaarOrPanInput', '');
      }
      prevDerivedPanRef.current = '';
    }
  }, [taxIdentityInput, form, isView, parsedLive.kind, parsedLive.value]);

  return (
    <div className="erp-customer-popup__pane">
      {/* Stage 6A.2E.3 — Identity row reorganised:
          LEFT panel  → GSTIN | GST Registration Type (equal half-cols)
                         + chip strip + inactive alert (full width).
          RIGHT panel → Aadhaar / PAN (full width). */}
      <Row gutter={8}>
        <Col xs={24} lg={12}>
          <div className="erp-customer-popup__panel">
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  label="GSTIN"
                  name="taxIdentityInput"
                  validateStatus={
                    /* Stage 6A.2F — green/red textbox background still
                       signals GSTIN portal status, but never in View
                       mode — we want a clean neutral read-only surface
                       there. */
                    isView
                      ? undefined
                      : gstinResult && (gstinResult.status === 'invalid' ||
                                        gstinResult.status === 'inactive' ||
                                        gstinResult.status === 'error')
                        ? 'error'
                        : (parsedLive.kind === 'unknown' && taxIdentityInput
                            ? 'error'
                            : (gstinResult && (gstinResult.status === 'valid' || gstinResult.status === 'local_valid')
                                ? 'success'
                                : undefined))
                  }
                  help={
                    gstinResult && gstinResult.status === 'invalid'
                      ? 'GSTIN is invalid per the GST portal.'
                      : gstinResult && gstinResult.status === 'inactive'
                        ? 'GSTIN is INACTIVE / Cancelled.'
                        : gstinResult && gstinResult.status === 'error'
                          ? 'GSTIN search failed — try again.'
                          : (parsedLive.kind === 'unknown' && taxIdentityInput
                              ? 'Enter a valid 15-char GSTIN or "URP" for unregistered.'
                              : undefined)
                  }
                  style={{ marginBottom: 4 }}
                  tooltip='15-char GSTIN, or the literal "URP" for unregistered customers. Mandatory — every customer must record one or the other.'
                  rules={[
                    // Stage 6A.2K-fix — every customer MUST record either
                    // a valid 15-char GSTIN or the literal "URP". Empty is
                    // no longer accepted: a blank GSTIN previously slipped
                    // through and stored NULL with no business signal as
                    // to whether the customer was unregistered or simply
                    // pending KYC.
                    {
                      required: true, whitespace: true,
                      message: 'GSTIN is required — enter a 15-char GSTIN or "URP" for unregistered customers.'
                    },
                    {
                      validator: (_r, v) => {
                        const s = String(v || '').trim();
                        if (!s) return Promise.resolve();   // required: above already fires
                        const k = parseGstinField(s).kind;
                        return (k === 'gstin' || k === 'urp')
                          ? Promise.resolve()
                          : Promise.reject(new Error('Enter a valid 15-char GSTIN or "URP".'));
                      }
                    }
                  ]}>
                  <Input
                    placeholder='27AAAPL1234C1Z5 · URP'
                    disabled={isView}
                    maxLength={15}
                    style={{ fontFamily: 'monospace' }}
                    onPressEnter={() => isGstin && onGstinSearch && onGstinSearch(taxIdentityInput)}
                    addonAfter={
                      <Button
                        type="link" size="small"
                        style={{ padding: 0, height: 22 }}
                        icon={<SearchOutlined />}
                        disabled={isView || !isGstin}
                        loading={gstinBusy}
                        onClick={() => onGstinSearch && onGstinSearch(taxIdentityInput)}>
                        Search
                      </Button>
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="GST Registration Type" name="gstRegistrationType"
                  rules={[{ required: true, message: 'Select a registration type.' }]}
                  tooltip="Auto-set when the GSTIN input is parsed. Override here if needed.">
                  <Select options={GST_REGISTRATION_TYPES} />
                </Form.Item>
              </Col>
            </Row>
            {/* Stage 6A.2E.4 — chip strip removed. The GSTIN textbox
                background itself signals valid (green) / invalid (red).
                The inactive alert below stays — it carries actionable
                guidance ("Use URP for unregistered parties"). */}
            {gstinResult && gstinResult.status === 'inactive' ? (
              <Alert type="error" showIcon style={{ marginBottom: 4 }}
                message="GSTIN is INACTIVE / Cancelled — save is blocked. Use URP for unregistered parties." />
            ) : null}
          </div>
        </Col>
        <Col xs={24} lg={12}>
          <div className="erp-customer-popup__panel">
            {/* Stage 6A.2E.3 — Aadhaar / PAN moved into the spot that
                previously held GST Registration Type. Behaviour
                unchanged: auto-filled from GSTIN, manually editable
                for URP customers; backend routes to `pan` or
                `aadhaarNumber` based on shape. */}
            <Form.Item
              label="Aadhaar / PAN"
              name="aadhaarOrPanInput"
              tooltip="Auto-filled with the GSTIN for registered customers. For URP customers, enter the 10-char PAN or 12-digit Aadhaar. Type NIL when the customer refuses to share KYC — Approval MFA is required."
              normalize={(v) => v ? String(v).toUpperCase() : v}
              rules={[
                // Stage 6A.2I — MANDATORY. whitespace:true rejects "   ".
                { required: true, whitespace: true, message: 'Aadhaar / PAN is required.' },
                {
                  validator: (_r, v) => {
                    const s = String(v || '').trim();
                    if (!s) return Promise.resolve(); // required rule above handles empty
                    const upper = s.toUpperCase();
                    // Stage 6A.2I — NIL / NILL is an explicit declaration
                    // accepted by the validator; the actual gate is the
                    // Approval MFA L1 step-up triggered at Save time.
                    if (upper === 'NIL' || upper === 'NILL') return Promise.resolve();
                    // Stage 6A.2E.4 — also accept a GSTIN (mirror of the
                    // GSTIN field for registered customers).
                    if (/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][A-Z][0-9A-Z]$/.test(upper)) return Promise.resolve();
                    if (/^X{4}-X{4}-[0-9]{4}$/.test(upper)) return Promise.resolve();
                    if (/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(upper)) return Promise.resolve();
                    const digits = s.replace(/[\s-]/g, '');
                    if (/^[0-9]{12}$/.test(digits)) return Promise.resolve();
                    return Promise.reject(new Error('Enter a valid GSTIN, 10-char PAN, 12-digit Aadhaar, or NIL.'));
                  }
                }
              ]}>
              <Input
                placeholder="27AAAPL1234C1Z5 · ABCDE1234F · 1234 5678 9012 · NIL"
                disabled={isView}
                maxLength={20}
                style={{ fontFamily: 'monospace' }} />
            </Form.Item>
            {/* Stage 6A.2I — NIL banner. Shown the moment the user types
                NIL / NILL into the field. The actual gate is the L1
                Approval MFA modal that opens at Save time.
                Stage 6A.2K bugfix — compact banner per spec. The Save
                button label also flips to "Save with Approval MFA" so
                the action is obvious without a long description. */}
            {isNilKyc && !isView ? (
              <Alert
                type="warning"
                showIcon
                banner
                style={{
                  marginTop: 4,
                  marginBottom: 4,
                  padding: '4px 8px',
                  lineHeight: 1.3
                }}
                message={
                  <span style={{ fontSize: 12 }}>
                    <strong>KYC marked NIL.</strong> Approval MFA required to save.
                  </span>
                }
              />
            ) : null}
          </div>
        </Col>
      </Row>

      <Row gutter={8}>
        {/* ═══════════ LEFT PANEL — identity + classification ═══════════ */}
        <Col xs={24} lg={12}>
          <div className="erp-customer-popup__panel">
            <Form.Item label="Customer Name" name="customerName"
              rules={[
                { required: true, message: 'Customer name is required.' },
                {
                  validator: (_r, v) => {
                    const s = (v || '').trim();
                    if (!s) return Promise.reject(new Error('Customer name cannot be blank.'));
                    if (s.length < 2 || s.length > 200) {
                      return Promise.reject(new Error('2-200 characters.'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}>
              <Input placeholder="Sri Balaji Toughened Glass" maxLength={200} />
            </Form.Item>
            <Form.Item label="Display Name" name="displayName"
              tooltip="Shown on invoices and dispatch documents. Defaults to Customer Name if left blank."
              rules={[{ max: 200, message: 'Up to 200 characters.' }]}>
              <Input maxLength={200} placeholder="Defaults to Customer Name if blank" />
            </Form.Item>
            {/* Stage 6A.2E.3 — Customer Type field removed.
                Stage 6A.2K — Category + Price Group sit side-by-side
                on a single row so the right-column rhythm stays even
                with the new field. Price Group is a placeholder
                dropdown today; the backend stores the value as a
                free-form VARCHAR(60). */}
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Category" name="customerCategory"
                  rules={[{ max: 50, message: 'Up to 50 characters.' }]}>
                  <Select allowClear showSearch
                    placeholder="Builder / Architect / Glazier..."
                    options={CUSTOMER_CATEGORY_SUGGESTIONS.map((c) => ({ value: c, label: c }))} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Price List"
                  name="priceListId"
                  tooltip="Which negotiated rate card prices this customer's quotations. Leave blank to quote the item master's standard rates."
                  extra={priceListLocked
                    ? 'On more than one card — manage it from the price list.'
                    : undefined}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={priceListsLoading}
                    disabled={isView || priceListLocked}
                    placeholder={priceLists.length ? 'Master rates (no price list)' : 'No price lists yet'}
                    notFoundContent={priceLists.length ? undefined : 'No price lists exist yet.'}
                    options={priceListOptions(priceLists, assignedList)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Parent Customer" name="parentCustomerId"
              tooltip="Group child accounts under a parent customer for reporting. Optional. Cannot reference this customer itself.">
              <Select
                allowClear showSearch
                placeholder="Search by code / name"
                loading={parentLoading}
                filterOption={false}
                onSearch={onParentSearch}
                options={parentOptions.map((p) => ({
                  value: p.id,
                  label: `${p.customerCode || ''} — ${p.customerName || ''}`,
                  data: p
                }))} />
            </Form.Item>

            {/* Salesperson (HRMS employee) + Customer Status side-by-side.
                The customer's salesperson is the HRMS employee (active +
                is_salesperson) — sourced from the HR employee master, NOT
                a login user. (The legacy user-based "Sales Representative"
                field was removed from this form; the sales_representative_id
                column is preserved untouched for existing records.) */}
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Salesperson" name="salespersonEmployeeId"
                  tooltip="Active HRMS employee marked as a salesperson.">
                  <Select
                    allowClear showSearch optionFilterProp="label"
                    disabled={isView}
                    placeholder="Search salesperson by name / code…"
                    loading={salespersonLoading}
                    options={salespersonOptions.map((e) => ({
                      value: e.id,
                      label: `${e.displayName || e.employeeName}${e.employeeCode ? ` (${e.employeeCode})` : ''}`,
                      data: e
                    }))}
                    optionRender={(option) => (
                      <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
                        <span style={{ fontWeight: 500 }}>{option.data.label}</span>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {option.data.data?.designationName || ''}
                          {option.data.data?.departmentName ? ` · ${option.data.data.departmentName}` : ''}
                        </Text>
                      </Space>
                    )} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Customer Status" name="customerStatusAxis"
                  rules={[{ required: true, message: 'Pick a status.' }]}>
                  {/* Stage 6A.2K-fix — axis value renamed from 'blacklist' to
                      'blocked' (tenant migration 0010 + backend constants).
                      Both UI and storage now use the same term. */}
                  <Select options={[
                    { value: 'active',  label: 'Active' },
                    { value: 'blocked', label: 'Blocked' }
                  ]} />
                </Form.Item>
              </Col>
            </Row>

            {/* Stage 6A.2E.5 — Remarks moved here (bottom of left
                column) so the right column ends cleanly with Email
                and the basic-info block reads top-to-bottom. */}
            <Form.Item label="Remarks" name="remarks"
              rules={[{ max: 2000 }]}>
              <Input.TextArea rows={2} maxLength={2000}
                placeholder="Notes for accounts / dispatch team..." />
            </Form.Item>
          </div>
        </Col>

        {/* ═══════════ RIGHT PANEL — address + contact + remarks ═══════════ */}
        <Col xs={24} lg={12}>
          <div className="erp-customer-popup__panel">
            <Form.Item label="Address Line 1" name="billingAddressLine1"
              rules={[
                // Stage 6A.2I — MANDATORY. whitespace:true rejects blank-after-trim.
                { required: true, whitespace: true, message: 'Address Line 1 is required.' },
                {
                  validator: (_r, v) => {
                    const s = String(v || '').trim();
                    if (!s) return Promise.resolve(); // required rule above handles empty
                    if (s.length > 250) return Promise.reject(new Error('Up to 250 characters.'));
                    return Promise.resolve();
                  }
                }
              ]}>
              <Input maxLength={250} placeholder="Plot / Building / Floor" />
            </Form.Item>
            <Form.Item label="Address Line 2" name="billingAddressLine2"
              rules={[{ max: 250 }]}>
              <Input maxLength={250} placeholder="Street / Landmark" />
            </Form.Item>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="City" name="billingCity"
                  rules={[
                    // Stage 6A.2I — MANDATORY. whitespace:true rejects blank-after-trim.
                    { required: true, whitespace: true, message: 'City is required.' },
                    {
                      validator: (_r, v) => {
                        const s = String(v || '').trim();
                        if (!s) return Promise.resolve(); // required rule above handles empty
                        if (s.length > 100) return Promise.reject(new Error('Up to 100 characters.'));
                        return Promise.resolve();
                      }
                    }
                  ]}>
                  <Input maxLength={100} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Pincode" name="billingPincode"
                  getValueFromEvent={digitsOnly(6)}
                  rules={[
                    // Stage 6A.2I — MANDATORY. Digits-only normalize already
                    // rejects letters; this rule enforces exactly 6 digits.
                    { required: true, message: 'Pincode is required.' },
                    {
                      validator: (_r, v) =>
                        /^[0-9]{6}$/.test(String(v || ''))
                          ? Promise.resolve()
                          : Promise.reject(new Error('Pincode must be exactly 6 digits.'))
                    }
                  ]}>
                  <Input maxLength={6} inputMode="numeric" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="State" name="billingStateCode"
              rules={[{ required: true, message: 'State is required.' }]}>
              <Select showSearch allowClear options={STATE_SELECT_OPTIONS}
                optionFilterProp="label" placeholder="Select state"
                onChange={(code) => {
                  if (code) {
                    form.setFieldValue('billingState', stateNameFromCode(code));
                    form.setFieldValue('gstStateCode', code);
                    form.setFieldValue('gstStateName', stateNameFromCode(code));
                  }
                }} />
            </Form.Item>

            {/* Stage 6A.2E.4 — Primary Mobile + Other Contact Numbers
                share a single row in the right panel column, each at
                half-width, to compact the contact section. */}
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Primary Mobile" name="mobile"
                  getValueFromEvent={digitsOnly(10)}
                  rules={[
                    // Stage 6A.2I — MANDATORY.
                    // Stage 6A.2K — strict 10-digit Indian mobile rule.
                    { required: true, message: 'Primary Mobile is required.' },
                    {
                      validator: (_r, v) =>
                        MOBILE_INDIA_REGEX.test(String(v || ''))
                          ? Promise.resolve()
                          : Promise.reject(new Error(
                              'Mobile number must be exactly 10 digits and start with 9, 8, 7, or 6.'
                            ))
                    }
                  ]}>
                  <Input maxLength={10} inputMode="numeric" placeholder="9876543210" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Other Contact Numbers" name="otherMobiles"
                  tooltip="Comma-separated; each must be exactly 10 digits starting with 9, 8, 7 or 6."
                  rules={[{
                    validator: (_r, v) => {
                      if (!v) return Promise.resolve();
                      const parts = String(v).split(',').map((s) => s.trim()).filter(Boolean);
                      for (let i = 0; i < parts.length; i += 1) {
                        // Stage 6A.2K — strict 10-digit Indian mobile per item.
                        if (!MOBILE_INDIA_REGEX.test(parts[i])) {
                          return Promise.reject(new Error(
                            `Invalid mobile number at position ${i + 1}. Use 10 digits starting with 9, 8, 7, or 6.`
                          ));
                        }
                      }
                      return Promise.resolve();
                    }
                  }]}>
                  <Input placeholder="9876543210, 9123456789" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Email" name="emailCsv"
              tooltip="Comma-separated emails."
              rules={[{
                validator: (_r, v) => {
                  if (!v) return Promise.resolve();
                  const parts = String(v).split(',').map((s) => s.trim()).filter(Boolean);
                  for (let i = 0; i < parts.length; i += 1) {
                    if (!isEmail(parts[i])) {
                      return Promise.reject(new Error(`Item ${i + 1} invalid: "${parts[i]}".`));
                    }
                  }
                  return Promise.resolve();
                }
              }]}>
              <Input placeholder="accounts@example.com, billing@example.com" />
            </Form.Item>
            {/* Stage 6A.2E.5 — Remarks relocated to the left column. */}
          </div>
        </Col>
      </Row>
    </div>
  );
}

// ─── Tab 2: GST & Accounting — identity + flags + credit + 2026 ───
//
// Stage 6A.2E reorganization: GST/Tax Identity input, GST Flags, and
// Credit & Payment Terms sections moved here from Customer Details.
// e_invoice_applicable, e_way_bill_applicable, and gst_filing_frequency
// are intentionally NOT rendered (backend still accepts them on
// payload — UI hidden per spec).
function GstAccountingTab({ form }) {
  const isSez = Form.useWatch('isSez', form);
  // Stage 6A.2I — `openingBalance` watcher removed: Balance Type is now
  // unconditionally required, so its rule no longer needs to peek at the
  // opening balance value.
  const msmeRegistered = Form.useWatch('msmeRegistered', form);
  const tdsApplicable  = Form.useWatch('tdsApplicable',  form);

  return (
    <div className="erp-customer-popup__pane">
      <Row gutter={8}>
        {/* ── LEFT — GST flags → Credit & Payment ───────────────── */}
        <Col xs={24} lg={12}>
          <div className="erp-customer-popup__panel">
            {/* Stage 6A.2E.1 — GST/Tax Identity + GST Registration
                Type moved to the top of Customer Details tab. */}
            <div className="erp-customer-popup__panel-subtitle">GST Flags</div>
            <Row gutter={8}>
              <Col span={8}>
                <Form.Item label="SEZ" name="isSez" valuePropName="checked">
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Composition" name="isCompositionDealer" valuePropName="checked">
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Reverse Charge" name="isReverseChargeApplicable" valuePropName="checked">
                  <Switch size="small" />
                </Form.Item>
              </Col>
            </Row>
            {isSez ? (
              <Form.Item label="SEZ Type" name="sezType">
                <Select allowClear options={SEZ_TYPES} />
              </Form.Item>
            ) : null}

            {/* Stage 6A.2E.5 — Default Ledger Group moved up from
                the right panel (Ledger section) to sit ABOVE the
                Credit & Payment block. The accounting bucket gates
                the receivables ledger, so showing it before the
                credit fields reads more naturally. */}
            <div className="erp-customer-popup__panel-subtitle">Ledger</div>
            <Form.Item label="Default Ledger Group" name="defaultLedgerGroup"
              tooltip="Accounting bucket used when posting receivables. Defaults to Sundry Debtors."
              rules={[{ max: 100 }]}>
              <Input maxLength={100} placeholder="Sundry Debtors" />
            </Form.Item>

            <div className="erp-customer-popup__panel-subtitle">Credit & Payment</div>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Credit Limit (₹)" name="creditLimit"
                  rules={[{ type: 'number', min: 0, message: 'Must be ≥ 0.' }]}>
                  <InputNumber min={0} precision={2} className="!w-full" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Credit Days" name="creditDays"
                  rules={[
                    { type: 'number', min: 0, max: 3650, message: '0-3650 days.' },
                    {
                      validator: (_r, v) =>
                        v === null || v === undefined || Number.isInteger(Number(v))
                          ? Promise.resolve()
                          : Promise.reject(new Error('Whole number only.'))
                    }
                  ]}>
                  <InputNumber min={0} max={3650} precision={0} className="!w-full" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Opening Balance (₹)" name="openingBalance"
                  rules={[{ type: 'number', min: 0, message: 'Must be ≥ 0.' }]}>
                  <InputNumber min={0} precision={2} className="!w-full" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Balance Type" name="openingBalanceType"
                  rules={[
                    // Stage 6A.2I — MANDATORY at all times (previously only
                    // when opening balance was non-zero). Defaults to 'debit'
                    // via DEFAULTS so the user starts with a valid pick.
                    { required: true, message: 'Balance Type is required.' },
                    {
                      validator: (_r, v) => {
                        // Defence-in-depth: reject anything other than the
                        // two allowed enum values even if a tampered Select
                        // option were submitted.
                        if (v === 'debit' || v === 'credit') return Promise.resolve();
                        return Promise.reject(new Error('Balance Type must be Debit or Credit.'));
                      }
                    }
                  ]}>
                  <Select options={[
                    { value: 'debit',  label: 'Debit' },
                    { value: 'credit', label: 'Credit' }
                  ]} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Payment Terms" name="paymentTerms"
              rules={[{ max: 2000 }]}>
              <Input.TextArea rows={2} maxLength={2000}
                placeholder="e.g. 50% advance, balance against PI..." />
            </Form.Item>
          </div>
        </Col>

        {/* ── RIGHT — TDS/TCS → MSME → Bank → Ledger ────────────── */}
        <Col xs={24} lg={12}>
          <div className="erp-customer-popup__panel">
            <div className="erp-customer-popup__panel-subtitle">
              TDS / TCS — Income-tax Act
            </div>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="TDS Applicable" name="tdsApplicable" valuePropName="checked"
                  tooltip="Tick when the buyer side must deduct TDS (Sec 51 / 194Q etc.) on payments to this customer.">
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="TDS Section" name="tdsSection"
                  tooltip="Required only when TDS Applicable is on.">
                  <Select allowClear showSearch options={TDS_SECTION_OPTIONS}
                    disabled={!tdsApplicable} placeholder="Pick a section" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="TCS Applicable" name="tcsApplicable" valuePropName="checked"
                  tooltip="Sec 206C(1H) — collect TCS when sales exceed ₹50L threshold to this customer.">
                  <Switch size="small" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Sec 206AB (Higher TDS)" name="section206abApplicable"
                  valuePropName="checked"
                  tooltip="Use higher TDS rate when the payee is a non-filer of ITR for 2 prior years.">
                  <Switch size="small" />
                </Form.Item>
              </Col>
            </Row>

            <div className="erp-customer-popup__panel-subtitle">
              MSME / Udyam — Sec 43B(h)
            </div>
            <Form.Item label="MSME Registered" name="msmeRegistered" valuePropName="checked"
              tooltip="Triggers Sec 43B(h) 45-day mandatory payment compliance.">
              <Switch size="small" />
            </Form.Item>
            <Row gutter={8}>
              <Col span={14}>
                <Form.Item label="Udyam Registration Number" name="udyamNumber"
                  tooltip="Format: UDYAM-XX-NN-NNNNNNN."
                  normalize={(v) => v ? String(v).toUpperCase() : v}
                  rules={[{
                    validator: (_r, v) => !v || /^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/.test(String(v))
                      ? Promise.resolve()
                      : Promise.reject(new Error('Format: UDYAM-XX-NN-NNNNNNN.'))
                  }]}>
                  <Input maxLength={25} placeholder="UDYAM-KA-01-0123456"
                    disabled={!msmeRegistered} style={{ fontFamily: 'monospace' }} />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item label="MSME Category" name="msmeCategory">
                  <Select allowClear options={MSME_CATEGORY_OPTIONS}
                    disabled={!msmeRegistered} placeholder="Pick" />
                </Form.Item>
              </Col>
            </Row>

            <div className="erp-customer-popup__panel-subtitle">
              Bank Details (Refund / Credit-note payout)
            </div>
            <Form.Item label="Bank Name" name="bankName" rules={[{ max: 150 }]}>
              <Input maxLength={150} placeholder="HDFC Bank" />
            </Form.Item>
            <Row gutter={8}>
              <Col span={14}>
                <Form.Item label="Account Number" name="bankAccountNumber"
                  rules={[{
                    validator: (_r, v) => !v || /^[0-9A-Za-z-]{4,40}$/.test(String(v))
                      ? Promise.resolve()
                      : Promise.reject(new Error('4-40 alphanumeric / hyphen.'))
                  }]}>
                  <Input maxLength={40} style={{ fontFamily: 'monospace' }} />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item label="IFSC Code" name="bankIfscCode"
                  normalize={(v) => v ? String(v).toUpperCase() : v}
                  rules={[{
                    validator: (_r, v) => !v || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(v))
                      ? Promise.resolve()
                      : Promise.reject(new Error('11 chars: 4 letters + 0 + 6 alphanum.'))
                  }]}>
                  <Input maxLength={11} placeholder="HDFC0001234" style={{ fontFamily: 'monospace' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Branch" name="bankBranch" rules={[{ max: 150 }]}>
              <Input maxLength={150} />
            </Form.Item>
            {/* Stage 6A.2E.5 — Default Ledger Group moved to left panel
                above Credit & Payment. */}
          </div>
        </Col>
      </Row>
    </div>
  );
}

// ─── Tab 5: Wastage Mode — customer-specific glass wastage ────
//
// Stage 6A.2E — moved into its own tab so the GST & Accounting tab
// stays focused on tax + credit. Round-off enum is enforced via the
// Select option list; +MM is an integer 0-999 (no decimals, no text).
function WastageModeTab() {
  return (
    <div className="erp-customer-popup__pane">
      <div className="erp-customer-popup__panel" style={{ maxWidth: 540 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          Customer-specific wastage settings used for glass size calculation.
        </Text>
        <Table
          size="small" bordered pagination={false}
          className="erp-customer-popup__wastage"
          rowKey="key"
          dataSource={[
            {
              key: 'round_off',
              mode: 'Round Off',
              w: <Form.Item noStyle name={['wastageDefaults', 'widthRoundOff']}>
                <Select size="small" style={{ width: '100%' }}
                  options={[0, 3, 6, 9, 12].map((n) => ({ value: n, label: String(n) }))} />
              </Form.Item>,
              h: <Form.Item noStyle name={['wastageDefaults', 'heightRoundOff']}>
                <Select size="small" style={{ width: '100%' }}
                  options={[0, 3, 6, 9, 12].map((n) => ({ value: n, label: String(n) }))} />
              </Form.Item>
            },
            {
              key: 'plus_mm',
              mode: '+ MM',
              w: <Form.Item noStyle name={['wastageDefaults', 'widthPlusMm']}
                rules={[{ type: 'integer', min: 0, max: 999 }]}>
                <InputNumber size="small" min={0} max={999} precision={0} style={{ width: '100%' }} />
              </Form.Item>,
              h: <Form.Item noStyle name={['wastageDefaults', 'heightPlusMm']}
                rules={[{ type: 'integer', min: 0, max: 999 }]}>
                <InputNumber size="small" min={0} max={999} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            }
          ]}
          columns={[
            { title: 'Mode', dataIndex: 'mode', width: 110 },
            { title: 'W',    dataIndex: 'w',    width: 110 },
            { title: 'H',    dataIndex: 'h',    width: 110 }
          ]} />
      </div>
    </div>
  );
}

// Stage 6A.2F — compact step labels (no descriptions). Two layouts:
//   • ADD_WIZARD_STEPS  — 3-step create flow (KYC → GST → Defaults)
//   • ALL_TABS          — full 5-tab layout for Edit and View
//
// Shipping Addresses and Contact Details are intentionally absent from
// the Add wizard. Empty companions are sent at save-time; addresses /
// contacts are managed afterwards via Edit mode.
const ADD_WIZARD_STEPS = [
  { key: 'details',        title: 'KYC' },
  { key: 'gst-accounting', title: 'GST & Accounting' },
  { key: 'wastage',        title: 'Customer Defaults' }
];
const ALL_TABS = [
  { key: 'details',        title: 'KYC' },
  { key: 'gst-accounting', title: 'GST & Accounting' },
  { key: 'addresses',      title: 'Shipping Addresses' },
  { key: 'contacts',       title: 'Contact Details' },
  { key: 'wastage',        title: 'Customer Defaults' }
];

// Stage 6A.2I — Per-step required-field map used by the wizard "Next"
// button. Validates ONLY the fields visible on the current step so the
// user can't tab past mandatory inputs (defence in depth — the final
// `handleSubmit` re-runs `validateFields()` across the whole form).
//
// Keep this in sync with the `required: true` rules in the JSX above. If
// a field is added/removed as mandatory, update the matching step entry.
const STEP_FIELDS = Object.freeze({
  details: [
    'customerName',
    // Stage 6A.2K-fix — GSTIN textbox is mandatory: every customer
    // must record either a 15-char GSTIN or the literal "URP".
    'taxIdentityInput',
    'aadhaarOrPanInput',
    // 'salesRepresentativeId' removed — the login-user Sales Rep field is
    // no longer on the form; the HRMS Salesperson is optional.
    'billingAddressLine1',
    'billingCity',
    'billingPincode',
    'billingStateCode',
    'mobile',
    'gstRegistrationType',
    'customerStatusAxis'
  ],
  'gst-accounting': ['openingBalanceType'],
  wastage: []
});

// ── price-list options that always contain the CURRENT value ────────
//
// AntD Select renders the raw `value` when no option matches it — for a UUID
// primary key that means the operator sees
// "5a1ae56a-275e-4636-babb-253287816c77" where a card name belongs.
//
// Two things cause that here, and the second is permanent:
//   1. customerAssignments() can resolve BEFORE assignable(), so the value is
//      set while the option list is still empty.
//   2. assignable() deliberately excludes ARCHIVED cards (status <> ARCHIVED),
//      so a customer still mapped to an archived card NEVER has a matching
//      option, no matter how long you wait.
//
// The assignment row already carries listCode/listName/status, so the current
// card is merged in from there when the catalogue does not contain it. An
// archived card is labelled as archived rather than silently looking normal —
// it prices nothing, and that is worth seeing on the customer record.
function priceListOptions(lists, assigned) {
  const opts = (lists || []).map((l) => ({
    value: l.id,
    label: `${l.listCode} — ${l.listName}`,
    // A DRAFT card can be chosen, but it prices nothing until someone
    // activates it — say so rather than let the operator think the mapping
    // is doing work.
    title: l.status === 'DRAFT' ? 'Draft — prices nothing until activated' : undefined
  }));
  if (!assigned || !assigned.priceListId) return opts;
  if (opts.some((o) => o.value === assigned.priceListId)) return opts;

  const archived = String(assigned.status || '').toUpperCase() === 'ARCHIVED';
  const name = assigned.listCode
    ? `${assigned.listCode} — ${assigned.listName || ''}`.replace(/\s+—\s*$/, '')
    : 'Unknown price list';
  return [{
    value: assigned.priceListId,
    label: archived ? `${name} (archived)` : name,
    title: archived
      ? 'This card is archived — it prices nothing. Move the customer to a live card.'
      : undefined
  }, ...opts];
}


// ─── Main Drawer component ────────────────────────────────────
export default function CustomerTabbedDrawer({
  open, mode, record, onClose, onSaved,
  // Stage 6A.2I — the parent page owns the Approval MFA hook + modal
  // slot (so it's a single shared modal). The drawer asks the parent
  // to run protected mutations via this callback whenever a save path
  // requires step-up — currently only NIL-KYC.
  runWithApproval
}) {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('details');
  const [addresses, setAddresses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [gstinBusy, setGstinBusy] = useState(false);
  const [gstinResult, setGstinResult] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [salesRepOptions, setSalesRepOptions] = useState([]);
  const [salesRepLoading, setSalesRepLoading] = useState(false);
  // HRMS salesperson (employee) dropdown state.
  const [salespersonOptions, setSalespersonOptions] = useState([]);
  const [salespersonLoading, setSalespersonLoading] = useState(false);
  // Stage 6A.2E — Parent customer dropdown state.
  const [parentOptions, setParentOptions] = useState([]);

  // Stage 6PL-1 — the Price List picker. A customer is mapped to a rate card
  // by writing a CUSTOMER-scope row in `price_list_assignments`, the very same
  // record a card's Assignments tab writes, so the two screens never disagree.
  const [priceLists, setPriceLists] = useState([]);
  const [assignedList, setAssignedList] = useState(null);
  const [priceListsLoading, setPriceListsLoading] = useState(false);
  // The model allows a customer on SEVERAL cards (different effective windows,
  // set up from a card). A single-select cannot express that, and offering one
  // would silently delete the rest — so with >1 the field goes read-only and
  // points at the price list instead.
  const [priceListLocked, setPriceListLocked] = useState(false);
  const initialPriceListRef = useRef(null);
  const [parentLoading, setParentLoading] = useState(false);
  const [savedHeaderCode, setSavedHeaderCode] = useState(null);
  const submittingRef = useRef(false);

  // Stage 6A.2K — top-level NIL watch so the footer Save button can
  // surface the "Save with Approval MFA" label when the Aadhaar /
  // PAN field is NIL/NILL. The KYC tab also watches the same field
  // for its inline Alert.
  const aadhaarOrPanInputTop = Form.useWatch('aadhaarOrPanInput', form);
  const isNilKycTop = (() => {
    const s = String(aadhaarOrPanInputTop || '').trim().toUpperCase();
    return s === 'NIL' || s === 'NILL';
  })();

  const isView = mode === 'view';
  const titleByMode = useMemo(() => ({
    add: 'Add customer',
    edit: 'Edit customer',
    view: 'Customer details'
  }), []);

  // Initial sales rep list — fetched on first open. Cheap (≤100 rows
  // capped server-side).
  const loadSalesReps = useCallback(async (q) => {
    setSalesRepLoading(true);
    try {
      const data = await customerApi.listSalesRepresentatives(q);
      setSalesRepOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.response?.data?.error?.message ||
        'Failed to load sales representatives.';
      message.error({ content: msg, key: 'sales-rep-lookup' });
    } finally {
      setSalesRepLoading(false);
    }
  }, []);

  // HRMS salespersons (active + is_salesperson). Loaded up-front (≤500)
  // so the edit-mode selected salesperson always renders by name.
  const loadSalespersons = useCallback(async (q) => {
    setSalespersonLoading(true);
    try {
      const data = await customerApi.listSalespersons(q);
      const rows = data && Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : []);
      setSalespersonOptions(rows);
    } catch (err) {
      const msg = err?.response?.data?.error?.message || 'Failed to load salespersons.';
      message.error({ content: msg, key: 'salesperson-lookup' });
    } finally {
      setSalespersonLoading(false);
    }
  }, []);

  // Stage 6A.2E — Parent customer lazy lookup. excludeId removes
  // the current row when editing (server-side guard).
  const loadParents = useCallback(async (q) => {
    setParentLoading(true);
    try {
      const data = await customerApi.listParentCustomers({
        q,
        excludeId: record && record.id ? record.id : undefined
      });
      setParentOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.response?.data?.error?.message ||
        'Failed to load parent customers.';
      message.error({ content: msg, key: 'parent-customer-lookup' });
    } finally {
      setParentLoading(false);
    }
  }, [record]);

  // Reset + hydrate on (re)open.
  useEffect(() => {
    if (!open) {
      submittingRef.current = false;
      return;
    }
    setActiveTab('details');
    setGstinResult(null);
    setSavedHeaderCode(null);
    loadSalesReps('');
    loadSalespersons('');
    loadParents('');

    if (mode === 'add' || !record) {
      form.resetFields();
      form.setFieldsValue(DEFAULTS);
      setAddresses([]);
      setContacts([]);
      return;
    }

    // Soft-fail on both: a price-list outage must never block editing a
    // customer, so the picker just stays empty.
    setPriceListsLoading(true);
    priceListApi.assignable()
      .then((ls) => setPriceLists(Array.isArray(ls) ? ls : []))
      .catch(() => setPriceLists([]))
      .finally(() => setPriceListsLoading(false));
    priceListApi.customerAssignments(record.id)
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setPriceListLocked(list.length > 1);
        const row = list.length === 1 ? list[0] : null;
        const current = row ? row.priceListId : null;
        initialPriceListRef.current = current;
        // Keep the whole assignment, not just the id. Two things need its
        // listCode/listName: this call can resolve BEFORE assignable() does, and
        // assignable() excludes ARCHIVED cards entirely. In both cases the Select
        // would hold a value with no matching option, and AntD then renders the
        // raw value - a bare UUID where a card name belongs.
        setAssignedList(row);
        form.setFieldsValue({ priceListId: current });
      })
      .catch(() => { initialPriceListRef.current = null; setAssignedList(null); });

    setLoadingDetail(true);
    customerApi.get(record.id)
      .then((data) => {
        const {
          addresses: addrs, contacts: cts, wastageDefaults, ...main
        } = data || {};
        form.resetFields();
        // Stage 6A.2E.4 — synthesise the two identity textboxes:
        //   • GSTIN field  → main.gstin OR "URP" when unregistered
        //   • Aadhaar/PAN  → mirrors the GSTIN when present;
        //                    otherwise on-file PAN or masked Aadhaar.
        let taxIdentityInput = '';
        if (main.gstin) {
          taxIdentityInput = main.gstin;
        } else if (main.gstRegistrationType === 'unregistered') {
          taxIdentityInput = 'URP';
        }
        let aadhaarOrPanInput = '';
        if (main.gstin) {
          aadhaarOrPanInput = main.gstin;
        } else if (main.pan) {
          aadhaarOrPanInput = main.pan;
        } else if (main.aadhaarOnFile && main.aadhaarMasked) {
          aadhaarOrPanInput = main.aadhaarMasked;
        }

        form.setFieldsValue({
          ...DEFAULTS,
          ...main,
          customerStatusAxis: main.customerStatusAxis ||
            (main.status === 'blocked' ? 'blocked' : 'active'),
          // Backend NEVER returns full Aadhaar; aadhaarOnFile + aadhaarMasked
          // are display-only hidden values used by parseTaxIdentity to
          // detect the "unchanged" case on save.
          taxIdentityInput,
          aadhaarOrPanInput,
          wastageDefaults: wastageDefaults
            ? {
                widthRoundOff: wastageDefaults.widthRoundOff,
                heightRoundOff: wastageDefaults.heightRoundOff,
                widthPlusMm: wastageDefaults.widthPlusMm,
                heightPlusMm: wastageDefaults.heightPlusMm
              }
            : DEFAULTS.wastageDefaults
        });
        setAddresses((addrs || []).map((a) => ({ ...a, _key: a.id })));
        setContacts((cts || []).map((c) => ({ ...c, _key: c.id })));
        setSavedHeaderCode(main.customerCode || null);
        // If the loaded customer has a sales rep, ensure the option
        // exists in the dropdown so the chip renders the name.
        if (main.salesRepresentativeId && main.salesRepresentativeName) {
          setSalesRepOptions((prev) => {
            const exists = prev.some((u) => u.id === main.salesRepresentativeId);
            if (exists) return prev;
            return [
              {
                id: main.salesRepresentativeId,
                displayName: main.salesRepresentativeName,
                username: null, email: null, mobile: null
              },
              ...prev
            ];
          });
        }
        // Stage 6A.2E — make sure the loaded customer's parent shows
        // a readable label in the dropdown chip without a second lookup.
        //
        // Stage 6A.2K-fix — we seed the option whenever a parentCustomerId
        // is present, even if the joined `parentCustomerName` came back
        // null (parent soft-deleted or LEFT JOIN missed it). Without
        // this, the Select had no matching option for the form value
        // and AntD fell back to rendering the raw UUID — that's the
        // "GUID shown instead of name" bug. The fallback label still
        // communicates "this row points at a parent we can't fully
        // resolve" without hiding the reference.
        if (main.parentCustomerId) {
          setParentOptions((prev) => {
            const exists = prev.some((p) => p.id === main.parentCustomerId);
            if (exists) return prev;
            const hasName = !!main.parentCustomerName;
            return [
              {
                id:           main.parentCustomerId,
                customerCode: main.parentCustomerCode || '',
                customerName: hasName
                  ? main.parentCustomerName
                  : '(parent unavailable)',
                displayName:  hasName
                  ? main.parentCustomerName
                  : '(parent unavailable)'
              },
              ...prev
            ];
          });
        }
      })
      .catch((err) => {
        message.error(
          err?.response?.data?.error?.message || 'Failed to load customer.'
        );
      })
      .finally(() => setLoadingDetail(false));
  }, [open, mode, record, form, loadSalesReps, loadParents]);

  // GSTIN search handler. Only fires when the single tax-identity
  // input parses as a valid GSTIN — the live-classification chip in
  // the tab disables the Search button for PAN / Aadhaar / URP /
  // unknown so we don't accidentally hit the GSTIN endpoint with
  // something else.
  const handleGstinSearch = useCallback(async (raw) => {
    if (isView) return;
    const gstin = String(raw || '').trim();
    if (!gstin) {
      message.warning('Enter a GSTIN to search.');
      return;
    }
    setGstinBusy(true);
    setGstinResult(null);
    try {
      const data = await customerApi.gstinSearch(gstin);
      setGstinResult(data);
      if (data && data.urp) {
        form.setFieldsValue({
          gstRegistrationType: 'unregistered',
          gstin: null,
          taxIdentityInput: 'URP',
          // URP toggle does NOT auto-clear the Aadhaar/PAN textbox —
          // the customer may still have a PAN on file.
          customerStatusAxis: 'active'
        });
        message.success('Marked as URP — Unregistered.');
        return;
      }
      if (data && (data.status === 'valid' || data.status === 'local_valid')) {
        // Prefill missing fields ONLY — never clobber user input.
        const cur = form.getFieldsValue();
        const c = data.customer || {};
        const patch = {};
        const setIfEmpty = (k, v) => { if (!cur[k] && v) patch[k] = v; };
        // GSTIN is stored in two places: the hidden `gstin` (backend
        // column) and the visible `taxIdentityInput` (form text). We
        // always sync taxIdentityInput so the chip stays "Verified".
        if (c.gstin) patch.taxIdentityInput = c.gstin;
        setIfEmpty('gstin', c.gstin);
        setIfEmpty('pan', c.pan);
        // Stage 6A.2E.4 — push the COMPLETE GSTIN into the visible
        // Aadhaar/PAN textbox (display mirror; backend still derives
        // PAN from main.gstin via the mapper).
        if (c.gstin) {
          const cur = form.getFieldValue('aadhaarOrPanInput') || '';
          if (!cur || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(cur)) {
            patch.aadhaarOrPanInput = c.gstin;
          }
        }
        // Legal / Trade name fields no longer shown — but still
        // persisted via hidden Form.Items if the GSTIN response
        // carries them. We rely on the hidden Form.Item path below
        // to keep them in form state.
        setIfEmpty('gstLegalName', c.gstLegalName);
        setIfEmpty('gstTradeName', c.gstTradeName);
        // Customer Name uses Trade Name first, then Legal Name.
        setIfEmpty('customerName', c.customerName || c.gstTradeName || c.gstLegalName);
        setIfEmpty('gstStateCode', c.gstStateCode);
        setIfEmpty('gstStateName', c.gstStateName);
        if (c.address) {
          setIfEmpty('billingAddressLine1', c.address.addressLine1);
          setIfEmpty('billingAddressLine2', c.address.addressLine2);
          setIfEmpty('billingCity', c.address.city);
          setIfEmpty('billingPincode', c.address.pincode);
          setIfEmpty('billingState', c.address.state || c.gstStateName);
          setIfEmpty('billingStateCode', c.address.stateCode || c.gstStateCode);
        }
        if (cur.gstRegistrationType === 'unregistered') {
          patch.gstRegistrationType = 'registered_regular';
        }
        form.setFieldsValue(patch);
        message.success('GSTIN details fetched.');
      } else if (data && data.status === 'invalid') {
        message.error('GSTIN is invalid per the GST portal.');
      } else if (data && data.status === 'inactive') {
        message.error('GSTIN is INACTIVE / Cancelled — save is blocked.');
      } else {
        message.warning('Could not classify GSTIN response.');
      }
    } catch (err) {
      const msg = err?.response?.data?.error?.message || 'GSTIN search failed.';
      message.error(msg);
    } finally {
      setGstinBusy(false);
    }
  }, [form, isView]);

  const handleSalesRepSearch = useCallback((q) => {
    loadSalesReps(q || '');
  }, [loadSalesReps]);

  const handleParentSearch = useCallback((q) => {
    loadParents(q || '');
  }, [loadParents]);

  // Stage 6A.2E.2 — compose payload from the TWO identity textboxes.
  //   • GSTIN textbox       → routes to main.gstin (15-char) or sets
  //                           gstRegistrationType=unregistered + gstin=null
  //                           when literal "URP" is typed.
  //   • Aadhaar / PAN box   → routes to either main.pan (10-char) or
  //                           main.aadhaarNumber (12-digit, encrypted
  //                           backend-side). A masked Aadhaar render
  //                           ("XXXX-XXXX-9012") is treated as
  //                           unchanged — neither field is touched
  //                           so the backend's on-file encrypted blob
  //                           is preserved (COLUMN_TO_CAMEL_KEY logic).
  function buildPayload(values) {
    // Stage 6A.2I — defence-in-depth mandatory-field check. The Form's
    // `required` rules block submit under normal flow, but DevTools /
    // programmatic tampering could mutate `values` after validation. If
    // any mandatory field is missing here we throw a synthetic AntD
    // errorFields shape so `handleSubmit`'s existing catch surfaces it
    // exactly like a real validation failure.
    const MANDATORY = {
      customerName:           'Customer Name',
      // Stage 6A.2K-fix — GSTIN OR "URP" is mandatory on every customer.
      // The user must record one or the other so the database always
      // carries a positive signal about the customer's GST status.
      taxIdentityInput:       'GSTIN',
      aadhaarOrPanInput:      'Aadhaar / PAN',
      billingAddressLine1:    'Address Line 1',
      billingCity:            'City',
      billingPincode:         'Pincode',
      billingStateCode:       'State',
      mobile:                 'Primary Mobile',
      openingBalanceType:     'Balance Type'
    };
    for (const k of Object.keys(MANDATORY)) {
      const v = values[k];
      const isEmpty = v === null || v === undefined ||
        (typeof v === 'string' && v.trim() === '');
      if (isEmpty) {
        const msg = `${MANDATORY[k]} is required.`;
        form.setFields([{ name: [k], errors: [msg] }]);
        const synthetic = new Error(msg);
        synthetic.errorFields = [{ name: [k], errors: [msg] }];
        throw synthetic;
      }
    }
    // Reject anything other than the allowed Balance Type enum.
    if (values.openingBalanceType !== 'debit' &&
        values.openingBalanceType !== 'credit') {
      const msg = 'Balance Type must be Debit or Credit.';
      form.setFields([{ name: ['openingBalanceType'], errors: [msg] }]);
      const synthetic = new Error(msg);
      synthetic.errorFields = [{ name: ['openingBalanceType'], errors: [msg] }];
      throw synthetic;
    }

    const {
      otherMobiles, emailCsv,
      taxIdentityInput, aadhaarOrPanInput,
      ...main
    } = values;

    // ── GSTIN field ──────────────────────────────────────────────
    const parsedGstin = parseGstinField(taxIdentityInput);
    if (parsedGstin.kind === 'gstin') {
      main.gstin = parsedGstin.value;
      if (!main.gstRegistrationType || main.gstRegistrationType === 'unregistered') {
        main.gstRegistrationType = 'registered_regular';
      }
      // Stage 6A.2K-fix — derive PAN + gst_state_code straight from the
      // GSTIN every time. Without this, a stale `pan` left in the form
      // from a previously-loaded customer would mismatch the new GSTIN
      // (chars 3-12) and the backend's `assertGstConsistency` would
      // reject with "pan does not match gstin". Same for the state-code
      // prefix (chars 0-1). The backend mapper is authoritative; we
      // align the payload here so the cross-field check passes.
      main.pan          = parsedGstin.value.slice(2, 12);
      main.gstStateCode = parsedGstin.value.slice(0, 2);
    } else if (parsedGstin.kind === 'urp') {
      main.gstin = null;
      main.gstRegistrationType = 'unregistered';
    } else if (parsedGstin.kind === 'empty') {
      // User cleared the GSTIN. Treat as URP unless the form set a
      // more specific registration type (composition / sez stay).
      main.gstin = null;
      if (!main.gstRegistrationType) main.gstRegistrationType = 'unregistered';
    }
    // `unknown` is caught by the field validator BEFORE we get here.

    // ── Aadhaar / PAN field ──────────────────────────────────────
    const parsedAp = parseTaxIdentity(aadhaarOrPanInput);
    if (parsedAp.kind === 'gstin') {
      // Stage 6A.2E.4 — the field is mirroring the GSTIN for a
      // registered customer. We DON'T touch main.pan from here; the
      // backend mapper derives PAN from main.gstin. Clear any stale
      // Aadhaar so a registered customer doesn't carry an Aadhaar.
      delete main.aadhaarNumber;
    } else if (parsedAp.kind === 'pan') {
      main.pan = parsedAp.value;
      if (parsedGstin.kind === 'gstin') delete main.aadhaarNumber;
    } else if (parsedAp.kind === 'aadhaar') {
      main.aadhaarNumber = parsedAp.value;
      // Aadhaar entered means URP — don't blank PAN here; backend
      // accepts both (PAN is optional alongside Aadhaar).
    } else if (parsedAp.kind === 'nil') {
      // Stage 6A.2I — explicit "KYC document not provided" declaration.
      // handleSubmit() routes this through Approval MFA L1 BEFORE the
      // request lands. The backend validator + mapper additionally
      // scrub PAN / Aadhaar to null even if a tampered payload tried
      // to smuggle them past us.
      main.pan = null;
      main.aadhaarNumber = null;
      main.kycNotProvided = true;
    } else if (parsedAp.kind === 'empty') {
      // Field cleared. Null PAN — backend's intent detection only
      // touches columns present in the payload.
      main.pan = null;
      // aadhaarNumber is omitted on purpose — backend preserves on-file.
    }
    // `aadhaar_unchanged` → leave both untouched (preserves on-file).

    // CSV → primary alternates merge (unchanged from prior stage).
    const alts = String(otherMobiles || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (alts.length) main.alternateMobile = alts[0];
    const emails = String(emailCsv || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (emails.length && !main.email) main.email = emails[0];

    // status ↔ axis sync
    if (main.customerStatusAxis === 'blocked') main.status = 'blocked';
    else main.status = 'active';

    // Remove read-only display fields from payload.
    delete main.aadhaarOnFile;
    delete main.aadhaarMasked;
    delete main.aadhaarLast4;
    delete main.salesRepresentativeName;
    // HRMS salesperson: keep salespersonEmployeeId (a real writable field);
    // drop the read-only assigned-at echo from the GET response.
    delete main.salespersonAssignedAt;
    // Stage 6A.2E — joined fields from the LIST/GET response shouldn't
    // round-trip back to the writer.
    delete main.parentCustomerName;
    delete main.parentCustomerCode;
    // Stage 6PL-1 — the price list is NOT a customer column. It is an
    // assignment row on the card, written by its own master-gated endpoint
    // after the customer saves. (The customer validator strips unknown keys
    // anyway, but leaving it in the payload would imply it means something
    // here.)
    delete main.priceListId;

    const addressesPayload = addresses.map(({ _key, ...rest }) => rest);
    const contactsPayload  = contacts.map(({ _key, ...rest }) => rest);

    return {
      ...main,
      addresses: addressesPayload,
      contacts:  contactsPayload,
      wastageDefaults: main.wastageDefaults
    };
  }

  async function handleSubmit() {
    if (isView || submittingRef.current) return;
    try {
      const values = await form.validateFields();
      if (gstinResult && gstinResult.status === 'inactive') {
        Modal.confirm({
          title: 'GSTIN is inactive',
          icon: <ExclamationCircleOutlined />,
          content: 'This GSTIN is cancelled / suspended per the GST portal. Save is blocked. Use URP for unregistered parties.',
          okText: 'Got it',
          cancelButtonProps: { style: { display: 'none' } }
        });
        return;
      }
      submittingRef.current = true;
      setSubmitting(true);
      const payload = buildPayload(values);

      // Stage 6A.2I — NIL-KYC save path. When the Aadhaar/PAN field
      // parses to NIL/NILL, buildPayload sets `kycNotProvided=true`.
      // We route the request through the parent page's Approval MFA
      // hook so the L1 step-up modal opens BEFORE the create/update
      // hits the backend. The matching backend middleware re-asserts
      // the challenge; this is defence-in-depth, not the only gate.
      const needsApproval = payload.kycNotProvided === true;
      let saved;
      if (needsApproval) {
        if (typeof runWithApproval !== 'function') {
          throw new Error('Approval MFA is not available in this context.');
        }
        const resourceLabel = mode === 'edit' && record
          ? `${record.customerCode || ''} · ${payload.customerName || record.customerName || ''}`.trim()
          : (payload.customerName || '(new customer)');
        saved = await runWithApproval({
          operationKey: 'customer.kyc.nil_accept',
          resourceType: 'customer',
          resourceId:   mode === 'edit' && record ? record.id : 'kyc-nil-create',
          operationLabel:
            mode === 'edit'
              ? 'Save customer with NIL KYC'
              : 'Create customer with NIL KYC',
          resourceLabel,
          action: (challengeId) =>
            mode === 'edit' && record
              ? customerApi.update(record.id, payload,
                  { approvalMfaChallengeId: challengeId })
              : customerApi.create(payload,
                  { approvalMfaChallengeId: challengeId })
        });
      } else {
        saved = mode === 'edit' && record
          ? await customerApi.update(record.id, payload)
          : await customerApi.create(payload);
      }
      // Stage 6A.2F — explicit hint after Add: Shipping Addresses and
      // Contact Details are not part of the create wizard; they're
      // managed in Edit mode after the customer record exists.
      message.success(
        mode === 'edit'
          ? 'Customer updated.'
          : 'Customer created. Use Edit to add Shipping Addresses and Contact Details.',
        mode === 'edit' ? 2 : 4
      );
      // ── Stage 6PL-1 — the price-list mapping ────────────────────
      // A SEPARATE, master-gated call, because it writes an assignment row on
      // the CARD, not a column on the customer. Only fired when the choice
      // actually changed, so saving an unrelated field does not churn two
      // price-list audit trails.
      //
      // It runs AFTER the customer save so a create has an id to map. If it
      // fails the customer is still saved — so this reports the failure
      // plainly rather than swallowing it, and names where to fix it, instead
      // of leaving the operator believing a rate card is attached when it is
      // not.
      const chosenList = values.priceListId || null;
      if (!priceListLocked && saved && saved.id && chosenList !== initialPriceListRef.current) {
        try {
          await priceListApi.mapCustomer(saved.id, chosenList);
          initialPriceListRef.current = chosenList;
        } catch (mapErr) {
          message.error(
            `The customer was saved, but the price list could not be attached: ${
              (mapErr && mapErr.response && mapErr.response.data && mapErr.response.data.error
                && mapErr.response.data.error.message) || 'unknown error'
            } You can set it from the price list's Assignments tab.`,
            8
          );
        }
      }

      if (saved && saved.customerCode) setSavedHeaderCode(saved.customerCode);
      onSaved && onSaved(saved);
    } catch (err) {
      // Stage 6A.2I — silently swallow user-cancelled Approval MFA.
      if (err && err.name === 'ApprovalMfaCancelled') return;
      if (err && err.errorFields) {
        // Stage 6A.2K-fix — frontend validation failures used to ONLY
        // switch tabs, leaving the operator with no visible feedback if
        // they were already on the failing tab. We now ALSO render the
        // full failing-field list in the same bulleted toast used for
        // backend rejections so silent saves are impossible.
        const firstField = err.errorFields[0]?.name?.[0];
        let targetStep = 'details';
        if (typeof firstField === 'string') {
          for (const [stepKey, fields] of Object.entries(STEP_FIELDS)) {
            if (fields.includes(firstField)) { targetStep = stepKey; break; }
          }
        }
        setActiveTab(targetStep);
        const FIELD_LABELS = {
          customerName:           'Customer Name',
          aadhaarOrPanInput:      'Aadhaar / PAN',
          taxIdentityInput:       'GSTIN',
          gstRegistrationType:    'GST Registration Type',
          salesRepresentativeId:  'Sales Representative',
          billingAddressLine1:    'Address Line 1',
          billingCity:            'City',
          billingPincode:         'Pincode',
          billingStateCode:       'State',
          mobile:                 'Primary Mobile',
          alternateMobile:        'Alternate Mobile',
          openingBalanceType:     'Balance Type',
          customerStatusAxis:     'Customer Status',
          email:                  'Email',
          otherMobiles:           'Other Contact Numbers',
          emailCsv:               'Email (CSV)'
        };
        const issues = err.errorFields.flatMap((f) => {
          const k = Array.isArray(f.name) ? f.name[0] : f.name;
          const label = FIELD_LABELS[k] || (typeof k === 'string' ? k : 'Field');
          const msgs = Array.isArray(f.errors) && f.errors.length
            ? f.errors
            : ['is required.'];
          return msgs.map((m) => `${label}: ${m}`);
        });
        message.error({
          content: (
            <span style={{ display: 'inline-block', textAlign: 'left' }}>
              {issues.length === 1 ? (
                issues[0]
              ) : (
                <>
                  <strong>{issues.length} issues blocked the save:</strong>
                  <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                    {issues.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </>
              )}
            </span>
          ),
          duration: Math.min(15, 4 + issues.length * 2)
        });
        // eslint-disable-next-line no-console
        console.error('[CustomerSave] frontend validation blocked save:', {
          firstField,
          targetStep,
          errorFields: err.errorFields,
          issues
        });
      } else {
        // Stage 6A.2K-fix — surface the full backend rejection so the
        // operator can act on it. Multi-clause Joi errors arrive as a
        // single `message` string joined with '; '. We split them back
        // out and render each clause on its own line in the toast so a
        // 5-field rejection isn't a wall of text.
        const respMsg  = err?.response?.data?.error?.message;
        const respCode = err?.response?.data?.error?.code;
        const details  = err?.response?.data?.error?.details;
        const issues = Array.isArray(details) && details.length
          ? formatCustomerBackendIssues(details)
          : (respMsg
              ? String(respMsg).split(/\s*;\s*/).map((s) => s.trim()).filter(Boolean)
              : ['Failed to save customer.']);
        message.error({
          content: (
            <span style={{ display: 'inline-block', textAlign: 'left' }}>
              {issues.length === 1 ? (
                issues[0]
              ) : (
                <>
                  <strong>{issues.length} issues blocked the save:</strong>
                  <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                    {issues.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </>
              )}
            </span>
          ),
          duration: Math.min(15, 4 + issues.length * 2)
        });
        // eslint-disable-next-line no-console
        console.error('[CustomerSave] backend rejected:', {
          status: err?.response?.status,
          code:   respCode,
          message: respMsg,
          details,
          issues
        });
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const headerTitle = (
    <Space size={8} align="center">
      <span>{titleByMode[mode]}</span>
      {savedHeaderCode ? (
        <Tag style={{
          fontFamily: 'monospace',
          fontSize: 11,
          paddingInline: 6,
          marginInlineEnd: 0
        }}>{savedHeaderCode}</Tag>
      ) : null}
    </Space>
  );

  // Stage 6A.2F — Add mode = 3-step wizard (KYC → GST → Customer
  // Defaults). Edit and View keep the full 5-tab layout. The active
  // step / tab share the same key namespace so the inner content
  // components don't have to branch.
  const isWizard = mode === 'add';
  const currentStepIdx = ADD_WIZARD_STEPS.findIndex((s) => s.key === activeTab);
  const safeStepIdx = currentStepIdx < 0 ? 0 : currentStepIdx;
  const isFirstStep = safeStepIdx === 0;
  const isLastStep  = safeStepIdx === ADD_WIZARD_STEPS.length - 1;

  // Stage 6A.2I — wizard "Next" now validates ONLY the visible step's
  // mandatory fields before advancing. AntD scrolls + highlights the
  // first failure inline; `handleSubmit` still re-runs full-form
  // validation on the final Save Customer click.
  const goNext = useCallback(async () => {
    const stepKey   = ADD_WIZARD_STEPS[safeStepIdx]?.key;
    const fieldList = stepKey ? STEP_FIELDS[stepKey] : null;
    if (Array.isArray(fieldList) && fieldList.length) {
      try {
        await form.validateFields(fieldList);
      } catch (_e) {
        // Validation errors render inline; stay on this step.
        return;
      }
    }
    const next = ADD_WIZARD_STEPS[safeStepIdx + 1];
    if (next) setActiveTab(next.key);
  }, [safeStepIdx, form]);

  // Stage 6A.2I — clicking a step marker directly. Backward jumps are
  // always allowed; forward jumps validate every intermediate step's
  // mandatory fields and bail to the first failing step on error.
  const handleStepClick = useCallback(async (idx) => {
    if (idx > safeStepIdx) {
      for (let i = safeStepIdx; i < idx; i += 1) {
        const stepKey   = ADD_WIZARD_STEPS[i]?.key;
        const fieldList = stepKey ? STEP_FIELDS[stepKey] : null;
        if (Array.isArray(fieldList) && fieldList.length) {
          try {
            await form.validateFields(fieldList);
          } catch (_e) {
            setActiveTab(ADD_WIZARD_STEPS[i].key);
            return;
          }
        }
      }
    }
    const target = ADD_WIZARD_STEPS[idx];
    if (target) setActiveTab(target.key);
  }, [safeStepIdx, form]);
  const goPrev = useCallback(() => {
    const prev = ADD_WIZARD_STEPS[safeStepIdx - 1];
    if (prev) setActiveTab(prev.key);
  }, [safeStepIdx]);

  // Stage 6A.2D-Modal — surface converted from right-side Drawer to a
  // centered Modal. The body height is capped at ~70vh so the modal
  // stays well within the viewport on 13" laptops; internal scroll
  // keeps the header + footer visible.
  // Stage 6A.2D-Popup — classic ERP footer: Save (primary) + Close.
  const footerNode = isView ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button icon={<CloseOutlined />} onClick={onClose}>Close</Button>
    </div>
  ) : isWizard ? (
    /* Wizard footer: Previous + (Next or Save) + Close. Save is only
       on the last step; intermediate steps push Next as the primary
       action. handleSubmit still runs full-form validation. */
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <Button icon={<CloseOutlined />} onClick={onClose} disabled={submitting}>
        Close
      </Button>
      <Space>
        <Button icon={<LeftOutlined />}
          onClick={goPrev}
          disabled={isFirstStep || submitting}>
          Previous
        </Button>
        {isLastStep ? (
          <Button type="primary" icon={<SaveOutlined />}
            loading={submitting} onClick={handleSubmit}>
            {/* Stage 6A.2K — flip label when NIL/NILL KYC is in play so
                the user knows the MFA dialog is about to open. */}
            {isNilKycTop ? 'Save with Approval MFA' : 'Save Customer'}
          </Button>
        ) : (
          <Button type="primary"
            onClick={goNext} disabled={submitting}>
            Next <RightOutlined />
          </Button>
        )}
      </Space>
    </div>
  ) : (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <Button type="primary" icon={<SaveOutlined />}
        loading={submitting} onClick={handleSubmit}>
        {/* Stage 6A.2K — flip label when NIL/NILL KYC is in play. */}
        {isNilKycTop ? 'Save with Approval MFA' : 'Save'}
      </Button>
      <Button icon={<CloseOutlined />}
        onClick={onClose} disabled={submitting}>
        Close
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={headerTitle}
      width={1080}
      centered
      maskClosable={!submitting}
      destroyOnClose
      className="erp-customer-modal"
      styles={{
        body: {
          padding: 0,
          background: '#fafafa',
          maxHeight: '70vh',
          overflowY: 'auto'
        }
      }}
      footer={footerNode}
    >
      <Spin spinning={loadingDetail} delay={150}>
        <Form
          form={form}
          layout="vertical"
          disabled={isView}
          initialValues={DEFAULTS}
          /* Stage 6A.2K-fix — MUST stay `preserve` (default true).
             The Add wizard mounts each step conditionally
             ({activeTab === 'details' && <CustomerDetailsTab/>}, etc.).
             With preserve=false, clicking Next would unmount the current
             step's Form.Items and purge every value the user had typed —
             so a validation failure that bounced the user back to step 1
             would show an entirely blank form. preserve=true keeps the
             field values in the form store across mount/unmount cycles. */
          preserve
          className={`erp-customer-drawer__form${isView ? ' is-view-mode' : ''}`}
        >
          {/* Hidden display-only fields populated from the GET response
              + GSTIN search. The new single-input layout doesn't show
              these as form fields, but the backend still stores them. */}
          <Form.Item name="aadhaarOnFile" hidden><Input /></Form.Item>
          <Form.Item name="aadhaarMasked" hidden><Input /></Form.Item>
          <Form.Item name="salesRepresentativeName" hidden><Input /></Form.Item>
          {/* Stage 6A.2D-Popup — invisible identity fields. Populated
              by GSTIN search + hydration; submitted to backend as
              part of the payload. */}
          <Form.Item name="gstin" hidden><Input /></Form.Item>
          <Form.Item name="pan" hidden><Input /></Form.Item>
          <Form.Item name="gstLegalName" hidden><Input /></Form.Item>
          <Form.Item name="gstTradeName" hidden><Input /></Form.Item>
          <Form.Item name="gstStateCode" hidden><Input /></Form.Item>
          <Form.Item name="gstStateName" hidden><Input /></Form.Item>
          <Form.Item name="billingState" hidden><Input /></Form.Item>

          {isWizard ? (
            <>
              {/* Stage 6A.2F — 3-step compact wizard for Add mode.
                  Sticky strip, small markers, no descriptions. */}
              <div className="erp-customer-wizard__nav">
                <Steps
                  current={safeStepIdx}
                  size="small"
                  responsive={false}
                  onChange={handleStepClick}
                  items={ADD_WIZARD_STEPS.map((s) => ({ title: s.title }))}
                />
              </div>
              {/* Stage 6A.2K-fix — every step's content is mounted at all
                  times; only the active step is visible (display:none on
                  the others). AntD's form.validateFields() only validates
                  Form.Items that are currently MOUNTED. Conditionally
                  rendering each step (with `{activeTab === 'foo' && …}`)
                  unmounts the inactive steps' Form.Items, which makes
                  validateFields skip their rules AND drop their values
                  from the returned `values` object — causing the synthetic
                  mandatory check in buildPayload to falsely report fields
                  like customerName as missing after the user moves past
                  step 1. Keeping every step mounted closes that gap. */}
              <div style={{ padding: '14px 10px 10px' }}>
                <div style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
                  <CustomerDetailsTab
                    form={form} mode={mode}
                    priceLists={priceLists}
                    priceListsLoading={priceListsLoading}
                    priceListLocked={priceListLocked}
                    assignedList={assignedList}
                    salesRepOptions={salesRepOptions}
                    salesRepLoading={salesRepLoading}
                    onSalesRepSearch={handleSalesRepSearch}
                    salespersonOptions={salespersonOptions}
                    salespersonLoading={salespersonLoading}
                    parentOptions={parentOptions}
                    parentLoading={parentLoading}
                    onParentSearch={handleParentSearch}
                    onGstinSearch={handleGstinSearch}
                    gstinBusy={gstinBusy}
                    gstinResult={gstinResult}
                  />
                </div>
                <div style={{ display: activeTab === 'gst-accounting' ? 'block' : 'none' }}>
                  <GstAccountingTab form={form} />
                </div>
                <div style={{ display: activeTab === 'wastage' ? 'block' : 'none' }}>
                  <WastageModeTab />
                </div>
              </div>
            </>
          ) : (
            /* Edit / View → full 5-tab layout. Sticky tabs nav via the
               existing `.erp-customer-modal .ant-tabs-nav` rule. */
            <Tabs
              activeKey={activeTab} onChange={setActiveTab}
              type="line"
              tabBarStyle={{ marginBottom: 0 }}
            >
              {/* Stage 6A.2K-fix — `forceRender` keeps every tab's
                  Form.Items mounted regardless of which tab is active, so
                  `form.validateFields()` always sees the full field set
                  (see the matching display:none block above for the Add
                  wizard's equivalent fix). */}
              <TabPane tab="KYC" key="details" forceRender>
                <div style={{ padding: '14px 10px 10px' }}>
                  <CustomerDetailsTab
                    form={form} mode={mode}
                    priceLists={priceLists}
                    priceListsLoading={priceListsLoading}
                    priceListLocked={priceListLocked}
                    assignedList={assignedList}
                    salesRepOptions={salesRepOptions}
                    salesRepLoading={salesRepLoading}
                    onSalesRepSearch={handleSalesRepSearch}
                    salespersonOptions={salespersonOptions}
                    salespersonLoading={salespersonLoading}
                    parentOptions={parentOptions}
                    parentLoading={parentLoading}
                    onParentSearch={handleParentSearch}
                    onGstinSearch={handleGstinSearch}
                    gstinBusy={gstinBusy}
                    gstinResult={gstinResult}
                  />
                </div>
              </TabPane>
              <TabPane tab="GST & Accounting" key="gst-accounting" forceRender>
                <div style={{ padding: '14px 10px 10px' }}>
                  <GstAccountingTab form={form} />
                </div>
              </TabPane>
              <TabPane tab="Shipping Addresses" key="addresses" forceRender>
                <div style={{ padding: '14px 10px 10px' }}>
                  <AddressesTable value={addresses} onChange={setAddresses}
                    readOnly={isView} />
                </div>
              </TabPane>
              <TabPane tab="Contact Details" key="contacts" forceRender>
                <div style={{ padding: '14px 10px 10px' }}>
                  <ContactsTable value={contacts} onChange={setContacts}
                    readOnly={isView} />
                </div>
              </TabPane>
              <TabPane tab="Customer Defaults" key="wastage" forceRender>
                <div style={{ padding: '14px 10px 10px' }}>
                  <WastageModeTab />
                </div>
              </TabPane>
            </Tabs>
          )}
        </Form>
      </Spin>
    </Modal>
  );
}
